import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    useGetInterestQuery,
    useGetProfileStrengthQuery,
    useSetInterestMutation,
} from "slice/user";
import { validations } from "utils/validations";

export const useOtherInterests = () => {
    const { user_id } = useAppSelector((state) => state.userAuth),
        { data, isFetching } = useGetInterestQuery(user_id, {
            refetchOnMountOrArgChange: true,
        }),
        [
            setInterest,
            { isLoading, data: createUpdateInterestDetailsResponse, isSuccess },
        ] = useSetInterestMutation(),
        [httpMethod, setHttpMethod] = useState("POST"),
        [isModalOpen, setIsModalOpen] = useState(false),
        [disabled, setDisabledState] = useState(false),
        [hasClickedOnNext, setHasClickedOnNext] = useState(false),
        [inputs, setInputs] = useState({
            my_story: "",
            interests: "",
        }),
        [inputErrors, setInputErrors] = useState({
            my_story: "",
            interests: "",
        });
    const { refetch: refetchProfileStrength } = useGetProfileStrengthQuery(
        user_id ?? "",
        {
            skip: !user_id,
        }
    );
    const navigateTo = useNavigate();
    const toggleModal = () => setIsModalOpen((prev) => !prev);

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target;
            setInputs((prevValues) => ({
                ...prevValues,
                [name]: value,
            }));
            setInputErrors((prevValues) => ({
                ...prevValues,
                [name]: validations(name, value),
            }));
        },
        []
    );

    const onSubmit = useCallback(() => {
        const myStoryError = validations("my_story", inputs.my_story);
        const interestsError = validations("interests", inputs.interests);

        if (myStoryError === "" && interestsError === "") {
            setInterest({
                ...inputs,
                interests: inputs.interests.split(","),
                user_id,
                method: httpMethod,
            }).then(() => {
                refetchProfileStrength();
            });
        } else {
            setInputErrors({
                my_story: myStoryError,
                interests: interestsError,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [httpMethod, inputs, setInterest, user_id]);

    const handleBack = () => {
        navigateTo(
            routeConstants.profile.editProfile.root +
                routeConstants.profile.editProfile.experienceDetails
        );
    };

    const handleSaveAndNext = () => {
        setHasClickedOnNext(true);
        onSubmit();
    };

    useEffect(() => {
        if (data && (data.interests.length || data.my_story.length)) {
            setInputs({
                my_story: data.my_story,
                interests: data.interests.join(","),
            });
            setHttpMethod("PUT");
        }
    }, [data]);

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Data saved successfully!",
            });

            hasClickedOnNext &&
                navigateTo(
                    routeConstants.profile.editProfile.root +
                        routeConstants.profile.editProfile.locationAndLanguages
                );
        }
    }, [hasClickedOnNext, isSuccess, navigateTo]);

    useEffect(() => {
        if (createUpdateInterestDetailsResponse) {
            setHttpMethod("PUT");
            setDisabledState(true);
        }
    }, [createUpdateInterestDetailsResponse]);

    return {
        inputs,
        handleInputChange,
        onSubmit,
        isFetching,
        isLoading,
        inputErrors,
        hasClickedOnNext,
        disabled,
        handleSaveAndNext,
        handleBack,
        isModalOpen,
        toggleModal,
    };
};
